import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  getHeatGridCameraOutcome,
  HEAT_GRID_READ_REASONS,
  MIDDLEWARE_REQUEST_TIMEOUT_MS,
} from '@repo/front-end-services';
import type { HeatGridOutcome } from '@repo/front-end-services';
import type { HeatGridCells } from '@repo/front-end-utils';
import type { HeatGridWindow } from '@repo/frontend-types';

import Config from '../../common/Config';
import { decodeHeatView, HEAT_VIEW_PARTS, mergeHeatPartAnswers } from './heatView';

/** Whole body (head, torso and legs), the full-screen picture's fixed view (`unified-camera-panel`, R5.5). */
const VIEW = 'WHOLE_BODY' as const;

export type CameraHeat =
  | { state: 'idle' }
  | { state: 'loading' }
  /** The read succeeded with no heat in the window. */
  | { state: 'empty' }
  | { state: 'ready'; cells: HeatGridCells }
  | { state: 'failed'; reason: string };

/**
 * A stream's people heat for the full-screen picture (`unified-camera-panel`, R5): whole body over
 * `heatWindow` — the person's remembered heat window (`onboarding-heatmap-remembered`, R8) — the
 * Observers step's read and its rules, through the same service. The picture has no window picker,
 * so the window cannot change while it is open; a change between opens is a new read.
 *
 * - **Bounded twice**, as the Observers read is: one `AbortController` per read with a wall-clock
 *   deadline of `MIDDLEWARE_REQUEST_TIMEOUT_MS`, and the service's own axios `timeout` inside. No
 *   retry of its own: `retry` is the person's.
 * - **All or nothing.** Whole body reads three parts; any part failing fails the read, and nothing
 *   partial is drawn.
 * - **Overtaken.** Each read has an id; an answer that is not the latest one's is dropped, and a
 *   stream change or unmount aborts the read in flight. A late answer for a closed picture is never
 *   set.
 * - **Logged** only on failure: the stream BID and the reason, never the body or the token.
 *
 * `rtspBid` null reads nothing (`idle`). The Observers step keeps its own inline read; converging the
 * two is a separate, behaviour-preserving change (backlog: *One reader for a camera's people heat*).
 */
export const useCameraHeat = (
  rtspBid: string | null,
  heatWindow: HeatGridWindow,
): CameraHeat & { retry: () => void } => {
  const { getAccessTokenSilently } = useAuth0();
  // Read at call time: a read is keyed on the stream, the window and Retry, never on the getter's identity.
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;
  const [heat, setHeat] = useState<CameraHeat>({ state: 'idle' });
  const requestIdRef = useRef(0);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    if (!rtspBid) {
      setHeat(prev => (prev.state === 'idle' ? prev : { state: 'idle' }));
      return undefined;
    }
    const controller = new AbortController();
    let deadlinePassed = false;
    // The token getter takes no signal, so a deadline passing while it is pending fails the read
    // here and disowns whatever the getter answers later (review).
    let awaitingToken = true;
    const timer = window.setTimeout(() => {
      deadlinePassed = true;
      controller.abort();
      if (awaitingToken && requestId === requestIdRef.current) {
        requestIdRef.current += 1;
        fail('token', HEAT_GRID_READ_REASONS.timedOut, 'timeout');
      }
    }, MIDDLEWARE_REQUEST_TIMEOUT_MS);
    setHeat({ state: 'loading' });

    /** Logged with where it failed (token, read, decode) and the service's code, never the body. */
    const fail = (phase: 'token' | 'read' | 'decode', reason: string, code?: string) => {
      console.warn('[camera-heat] heat read failed for stream', rtspBid, phase, code ?? '', reason);
      setHeat({ state: 'failed', reason });
    };

    void (async () => {
      let token: string;
      try {
        token = await getTokenRef.current();
        awaitingToken = false;
      } catch (error) {
        awaitingToken = false;
        if (requestId !== requestIdRef.current) return;
        window.clearTimeout(timer);
        fail('token', (error as Error)?.message ?? HEAT_GRID_READ_REASONS.unreachable);
        return;
      }
      if (requestId !== requestIdRef.current) return;
      const parts = HEAT_VIEW_PARTS[VIEW];
      const outcomes = await Promise.all(
        parts.map(part =>
          getHeatGridCameraOutcome(
            Config.middlewareApi,
            token,
            rtspBid,
            heatWindow,
            part,
            controller.signal,
          ).catch(
            (error: unknown): HeatGridOutcome => ({
              status: 'failed',
              code: 'network',
              reason: (error as Error)?.message ?? HEAT_GRID_READ_REASONS.unreachable,
            }),
          ),
        ),
      );
      window.clearTimeout(timer);
      // Superseded (another stream, a retry, or the picture closed): this answer is not wanted.
      if (requestId !== requestIdRef.current) return;
      if (deadlinePassed) {
        fail('read', HEAT_GRID_READ_REASONS.timedOut, 'timeout');
        return;
      }
      const failed = outcomes.find(o => o.status !== 'ready');
      if (failed) {
        if (failed.status === 'failed') fail('read', failed.reason, failed.code);
        else fail('read', HEAT_GRID_READ_REASONS.unreachable, failed.status);
        return;
      }
      // Merged and decoded together: either throwing fails the read, never leaves it loading.
      let cells: HeatGridCells | null;
      try {
        const merged = mergeHeatPartAnswers(
          outcomes.map(o => (o as Extract<HeatGridOutcome, { status: 'ready' }>).value),
        );
        cells = decodeHeatView(merged, VIEW);
      } catch (error) {
        fail('decode', (error as Error)?.message ?? 'unreadable heat');
        return;
      }
      setHeat(cells ? { state: 'ready', cells } : { state: 'empty' });
    })();

    return () => {
      // Closing the picture or changing the stream: abort, and disown the answer.
      requestIdRef.current += 1;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [rtspBid, heatWindow, attempt]);

  const retry = useCallback(() => setAttempt(a => a + 1), []);
  return { ...heat, retry };
};
